import type { AnalyticsContext, AnalyticsScreen, DisplayMode, ViewportClass } from "./analyticsTypes";

export const INSTALL_ID_KEY = "moveinreport.analytics.installId";
let sessionId: string | undefined;
let sequence = 0;
let memoryInstallId: string | undefined;

export function nextAnalyticsSequence(): number {
  sequence += 1;
  return sequence;
}

export function getAnalyticsInstallId(): string {
  try {
    const existing = window.localStorage.getItem(INSTALL_ID_KEY);
    if (existing) {
      return existing;
    }
    const next = createAnalyticsId();
    window.localStorage.setItem(INSTALL_ID_KEY, next);
    return next;
  } catch {
    memoryInstallId ??= createAnalyticsId();
    return memoryInstallId;
  }
}

export function clearAnalyticsInstallId(): void {
  memoryInstallId = undefined;
  try {
    window.localStorage.removeItem(INSTALL_ID_KEY);
  } catch {
    // Local cleanup is best-effort and must never affect app capture.
  }
}

export function getAnalyticsSessionId(): string {
  sessionId ??= createAnalyticsId();
  return sessionId;
}

export function getAnalyticsContext(input: {
  screen?: AnalyticsScreen;
  routePattern?: string;
} = {}): AnalyticsContext {
  return {
    screen: input.screen,
    routePattern: input.routePattern,
    viewportClass: getViewportClass(),
    browserFamily: getBrowserFamily(),
    platformFamily: getPlatformFamily(),
    online: navigator.onLine,
    displayMode: getDisplayMode(),
    host: window.location.host
  };
}

export function getViewportClass(width = window.innerWidth): ViewportClass {
  if (!Number.isFinite(width)) {
    return "unknown";
  }
  if (width < 720) {
    return "mobile";
  }
  if (width < 1024) {
    return "tablet";
  }
  return "desktop";
}

export function getBrowserFamily(userAgent = navigator.userAgent): string {
  if (/Edg\//.test(userAgent)) {
    return "edge";
  }
  if (/Firefox\//.test(userAgent)) {
    return "firefox";
  }
  if (/Chrome\//.test(userAgent) || /CriOS\//.test(userAgent)) {
    return "chrome";
  }
  if (/Safari\//.test(userAgent)) {
    return "safari";
  }
  return "other";
}

export function getPlatformFamily(userAgent = navigator.userAgent): string {
  if (/Android/i.test(userAgent)) {
    return "android";
  }
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return "ios";
  }
  if (/Windows/i.test(userAgent)) {
    return "windows";
  }
  if (/Macintosh|Mac OS X/i.test(userAgent)) {
    return "macos";
  }
  if (/Linux/i.test(userAgent)) {
    return "linux";
  }
  return "other";
}

function getDisplayMode(): DisplayMode {
  const standaloneNavigator = navigator as Navigator & { standalone?: boolean };
  if (standaloneNavigator.standalone) {
    return "installed";
  }
  if (window.matchMedia?.("(display-mode: standalone)").matches) {
    return "standalone";
  }
  return "browser";
}

export function createAnalyticsId(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `analytics_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}
